/**
 * Word Editor Component
 * 
 * A rich text editor for Word documents (DOCX) using Tiptap.
 * Provides MS Word-like editing experience for scientific writing.
 */

import * as React from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { useEditorStore } from '../../stores/editorStore'
import { useDocumentStore } from '../../stores/documentStore'
import { useAIStore } from '@stores/aiStore'
import { useQueryClient } from '@tanstack/react-query'
import WordToolbar from './WordToolbar'
import SelectionToolbar from './SelectionToolbar'
import FloatingAIChat, { FloatingAIChatHandle } from './FloatingAIChat'
import AIProcessingIndicator from './AIProcessingIndicator'
import type { TextSelection } from '@/types'
import { logError } from '@utils/logger'
import clsx from 'clsx'

interface WordEditorProps {
  className?: string
}

const WordEditor: React.FC<WordEditorProps> = ({ className }) => {
  const { 
    content, 
    setContent, 
    mode,
    selection,
    setSelection,
    setCursorPosition,
    cursorPosition,
  } = useEditorStore()
  const { currentDocument, updateDocument } = useDocumentStore()
  const { isProcessing, processAITask: processAITaskFromStore } = useAIStore()
  const queryClient = useQueryClient()
  const [isComposing, setIsComposing] = React.useState(false)
  const lastSavedContentRef = React.useRef<string>('')
  const [showSelectionToolbar, setShowSelectionToolbar] = React.useState(false)
  const [toolbarPosition, setToolbarPosition] = React.useState({ x: 0, y: 0 })
  const [currentTaskType, setCurrentTaskType] = React.useState<string | null>(null)
  const editorContainerRef = React.useRef<HTMLDivElement | null>(null)
  const toolbarRef = React.useRef<HTMLDivElement | null>(null)
  const chatRef = React.useRef<FloatingAIChatHandle | null>(null)

  // Local theme for Word editor (light/dark), independent of global app theme
  const [wordTheme, setWordTheme] = React.useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light'
    const stored = window.localStorage.getItem('word_editor_theme')
    return stored === 'dark' ? 'dark' : 'light'
  })

  // Rich insertion panels state
  const [showLinkDialog, setShowLinkDialog] = React.useState(false)
  const [linkUrl, setLinkUrl] = React.useState('')
  const [linkText, setLinkText] = React.useState('')

  const [showImageDialog, setShowImageDialog] = React.useState(false)
  const [imageUrl, setImageUrl] = React.useState('')
  const [imageFile, setImageFile] = React.useState<File | null>(null)

  const [showTableDialog, setShowTableDialog] = React.useState(false)
  const [tableRows, setTableRows] = React.useState(3)
  const [tableCols, setTableCols] = React.useState(3)

  // Initialize Tiptap editor with extensions
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable default heading to use custom ones
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Start typing your document...',
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-gray-300',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content || '<p></p>',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      // Only update if content actually changed to avoid infinite loops
      if (html !== content) {
        setContent(html)
      }
    },
    editorProps: {
      attributes: {
        class: clsx(
          'prose max-w-none focus:outline-none',
          // When in dark theme, invert prose colors
          wordTheme === 'dark' && 'prose-invert'
        ),
      },
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection

      if (from !== to) {
        const selectedText = editor.state.doc.textBetween(from, to, '\n')

        const textSelection: TextSelection = {
          start: from,
          end: to,
          text: selectedText,
          lineStart: 0,
          lineEnd: 0,
        }

        setSelection(textSelection)

        // Position toolbar near the DOM selection
        const domSelection = window.getSelection()
        if (domSelection && domSelection.rangeCount > 0 && editorContainerRef.current) {
          const range = domSelection.getRangeAt(0)
          const rect = range.getBoundingClientRect()
          const containerRect = editorContainerRef.current.getBoundingClientRect()

          if (rect.width || rect.height) {
            const estimatedWidth = 500
            let x = rect.left - containerRect.left + rect.width / 2 - estimatedWidth / 2
            let y = rect.top - containerRect.top - 56 // place above selection

            const padding = 10
            x = Math.max(padding, Math.min(x, containerRect.width - estimatedWidth - padding))
            if (y < padding) {
              y = rect.bottom - containerRect.top + padding
            }

            setToolbarPosition({ x, y })
            setShowSelectionToolbar(true)

            // After render, adjust to actual width
            requestAnimationFrame(() => {
              if (!toolbarRef.current || !editorContainerRef.current) return
              const tbRect = toolbarRef.current.getBoundingClientRect()
              const contRect = editorContainerRef.current.getBoundingClientRect()
              let adjustedX = rect.left - contRect.left + rect.width / 2 - tbRect.width / 2
              adjustedX = Math.max(padding, Math.min(adjustedX, contRect.width - tbRect.width - padding))
              setToolbarPosition(prev => ({ ...prev, x: adjustedX }))
            })
          }
        }
      } else {
        // Collapse selection: hide toolbar and update cursor position
        setSelection(null)
        setShowSelectionToolbar(false)
        setCursorPosition(to)
      }
    },
  })

  // Persist word editor theme
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('word_editor_theme', wordTheme)
  }, [wordTheme])

  // Update editor attributes when theme changes
  React.useEffect(() => {
    if (!editor) return
    editor.setOptions({
      editorProps: {
        attributes: {
          class: clsx(
            'prose max-w-none focus:outline-none',
            wordTheme === 'dark' && 'prose-invert'
          ),
        },
      },
    })
  }, [editor, wordTheme])

  // Update editor content when external content changes (but not from our own updates)
  React.useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML()
      // Only update if content is different and not empty
      if (content && content !== currentHtml && content.trim() !== '<p></p>') {
        editor.commands.setContent(content)
      } else if (!content || content.trim() === '') {
        // If content is empty, set to empty paragraph
        if (currentHtml.trim() !== '<p></p>') {
          editor.commands.setContent('<p></p>')
        }
      }
    }
  }, [content, editor])

  // Initialize last saved content when document loads
  React.useEffect(() => {
    if (currentDocument && content) {
      lastSavedContentRef.current = content
    }
  }, [currentDocument?.id]) // Only when document changes, not on every content change

  // Auto-save on content change (debounced)
  React.useEffect(() => {
    if (!currentDocument || mode !== 'word' || isComposing || !editor || !content) return

    // Skip if content hasn't actually changed from last saved version
    if (content === lastSavedContentRef.current) {
      return
    }

    // Skip if content is just empty paragraph
    const trimmedContent = content.trim()
    if (trimmedContent === '<p></p>' || trimmedContent === '') {
      return
    }

    // Debounce auto-save - wait 8 seconds after user stops typing
    const timeoutId = setTimeout(() => {
      // Double-check content still changed (user might have undone changes)
      if (content !== lastSavedContentRef.current && currentDocument) {
        updateDocument(currentDocument.id, {
          content: content,
        })
          .then(() => {
            // Update last saved content only on successful save
            lastSavedContentRef.current = content
          })
          .catch((err) => {
            console.error('Failed to auto-save Word document:', err)
          })
      }
    }, 8000) // 8 second debounce - standard for word processors

    return () => clearTimeout(timeoutId)
  }, [content, currentDocument, mode, isComposing, updateDocument, editor])

  // Handlers for rich insertion dialogs
  const openLinkDialog = () => {
    if (!editor) return
    const { from, to } = editor.state.selection
    const selected = editor.state.doc.textBetween(from, to, '\n')
    const previousUrl = editor.getAttributes('link').href as string | undefined
    setLinkText(selected || '')
    setLinkUrl(previousUrl || '')
    setShowLinkDialog(true)
  }

  const confirmLinkInsert = () => {
    if (!editor) return
    if (!linkUrl.trim()) {
      // If URL cleared, remove link mark
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setShowLinkDialog(false)
      return
    }

    const url = linkUrl.trim()
    const text = linkText.trim() || url
    const { from, to, empty } = editor.state.selection
    const chain = editor.chain().focus()

    if (empty) {
      // Insert new linked text at cursor
      chain
        .insertContent(text)
        .setTextSelection({ from, to: from + text.length })
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()
    } else {
      // Apply link to existing selection (optionally replace text)
      if (linkText && linkText.trim() && linkText.trim() !== editor.state.doc.textBetween(from, to, '\n')) {
        chain.insertContentAt({ from, to }, text)
      }
      chain.extendMarkRange('link').setLink({ href: url }).run()
    }

    setShowLinkDialog(false)
  }

  const openImageDialog = () => {
    setImageUrl('')
    setImageFile(null)
    setShowImageDialog(true)
  }

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImageUrl('')
    }
  }

  const confirmImageInsert = () => {
    if (!editor) return
    const url = imageUrl.trim()

    if (!url && !imageFile) {
      setShowImageDialog(false)
      return
    }

    const insertFromUrl = (src: string) => {
      editor.chain().focus().setImage({ src }).run()
      setShowImageDialog(false)
    }

    if (url) {
      insertFromUrl(url)
      return
    }

    if (imageFile) {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result
        if (typeof result === 'string') {
          insertFromUrl(result)
        } else {
          setShowImageDialog(false)
        }
      }
      reader.readAsDataURL(imageFile)
    }
  }

  const openTableDialog = () => {
    setShowTableDialog(true)
  }

  const confirmTableInsert = () => {
    if (!editor) return
    const safeRows = Number.isFinite(tableRows) ? tableRows : 1
    const safeCols = Number.isFinite(tableCols) ? tableCols : 1
    const rows = Math.max(1, Math.min(20, safeRows))
    const cols = Math.max(1, Math.min(20, safeCols))
    try {
      // Clear any existing selection so the table is inserted cleanly
      editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()
    } catch (error) {
      logError('Failed to insert table:', error)
    } finally {
      setShowTableDialog(false)
    }
  }

  // Handle AI tasks for Word editor
  const handleAITask = React.useCallback(
    async (taskType: string, text: string, instructions?: string) => {
      if (!selection) return

      const docId = currentDocument?.id
      setCurrentTaskType(taskType)

      try {
        // Explain goes to chat instead of in-place replacement
        if (taskType === 'explain') {
          const response = await processAITaskFromStore(
            { text, instructions, document_id: docId },
            taskType as any
          )

          if (chatRef.current) {
            const userMessage = `Explain this: ${text}`
            await chatRef.current.openWithContext(text, userMessage, response.result)
            chatRef.current.showNotification()
          }
        } else {
          // Other tasks: replace selected text with suggestion
          const response = await processAITaskFromStore(
            { text, instructions, document_id: docId },
            taskType as any
          )

          if (editor) {
            editor
              .chain()
              .focus()
              .insertContentAt(
                { from: selection.start, to: selection.end },
                response.result || '',
              )
              .run()
          }
        }

        if (docId) {
          queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', docId] })
        }
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })
      } catch (error) {
        logError('WordEditor AI task failed:', error)
      } finally {
        setCurrentTaskType(null)
      }
    },
    [selection, currentDocument?.id, editor, processAITaskFromStore, queryClient],
  )

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={editorContainerRef}
      className={clsx('relative h-full w-full flex flex-col', className)}
    >
      <WordToolbar
        editor={editor}
        theme={wordTheme}
        onInsertLink={openLinkDialog}
        onInsertImage={openImageDialog}
        onInsertTable={openTableDialog}
      />
      <div
        className={clsx(
          'flex-1 overflow-auto',
          wordTheme === 'dark'
            ? 'bg-slate-900'
            : 'bg-slate-100'
        )}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
      >
        <div className="min-h-full flex justify-center py-10 px-4 sm:px-6 lg:px-8">
          <div
            className={clsx(
              'w-full max-w-4xl rounded-xl border shadow-sm transition-colors',
              wordTheme === 'dark'
                ? 'bg-slate-950 border-slate-700 text-gray-100'
                : 'bg-white border-gray-200 text-gray-900'
            )}
          >
            <div className="p-8 sm:p-10">
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>
      </div>

      {/* Single theme toggle in footer (bottom-left), away from floating chat */}
      <button
        type="button"
        onClick={() => setWordTheme(prev => (prev === 'light' ? 'dark' : 'light'))}
        className={clsx(
          'absolute bottom-3 left-4 flex items-center gap-2 rounded-full px-3 py-1 text-xs shadow-sm border',
          wordTheme === 'dark'
            ? 'bg-gray-900 text-gray-100 border-gray-600'
            : 'bg-white text-gray-700 border-gray-200'
        )}
        title="Toggle editor light/dark theme"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
        <span>
          Editor theme: {wordTheme === 'light' ? 'Light' : 'Dark'}
        </span>
      </button>
      {/* Selection AI Toolbar */}
      {showSelectionToolbar && selection && (
        <div
          ref={toolbarRef}
          className="absolute z-40 bg-white dark:bg-gray-900 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 p-2"
          style={{
            left: `${toolbarPosition.x}px`,
            top: `${toolbarPosition.y}px`,
            maxWidth: 'calc(100% - 20px)',
          }}
        >
          <SelectionToolbar
            selectedText={selection.text}
            onAITask={handleAITask}
            isProcessing={isProcessing}
            documentContext={content}
            documentMode="plain"
            documentId={currentDocument?.id || null}
          />
        </div>
      )}

      {/* AI Processing Indicator */}
      <AIProcessingIndicator
        isVisible={isProcessing}
        taskType={currentTaskType || undefined}
      />

      {/* Floating AI Chat (bottom-right icon & panel) */}
      <FloatingAIChat
        ref={chatRef}
        documentId={currentDocument?.id}
        cursorPosition={cursorPosition}
      />

      {/* Link insertion dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className={clsx(
            'w-full max-w-md rounded-xl shadow-xl border p-6 space-y-4',
            wordTheme === 'dark'
              ? 'bg-slate-900 border-slate-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-900'
          )}>
            <h3 className="text-lg font-semibold">Insert Link</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Link text
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className={clsx(
                    'mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    wordTheme === 'dark'
                      ? 'bg-slate-800 border-slate-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  )}
                  placeholder="Text to display for the hyperlink"
                />
              </label>
              <label className="block text-sm font-medium">
                URL
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className={clsx(
                    'mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    wordTheme === 'dark'
                      ? 'bg-slate-800 border-slate-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  )}
                  placeholder="https://example.com"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowLinkDialog(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLinkInsert}
                className="px-3 py-1.5 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image insertion dialog */}
      {showImageDialog && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className={clsx(
            'w-full max-w-md rounded-xl shadow-xl border p-6 space-y-4',
            wordTheme === 'dark'
              ? 'bg-slate-900 border-slate-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-900'
          )}>
            <h3 className="text-lg font-semibold">Insert Image</h3>
            <p className="text-xs text-gray-500">
              Use an image URL or upload from your device.
            </p>
            <div className="space-y-3">
              <label className="block text-sm font-medium">
                Image URL
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value)
                    if (e.target.value) setImageFile(null)
                  }}
                  className={clsx(
                    'mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    wordTheme === 'dark'
                      ? 'bg-slate-800 border-slate-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  )}
                  placeholder="https://example.com/image.png"
                />
              </label>
              <div className="flex items-center gap-3">
                <div>
                  <input
                    id="word-image-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileChange}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('word-image-file') as HTMLInputElement | null
                      input?.click()
                    }}
                    className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    Choose file…
                  </button>
                </div>
                {imageFile && (
                  <span className="text-xs text-gray-500 truncate max-w-[180px]">
                    {imageFile.name}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowImageDialog(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmImageInsert}
                className="px-3 py-1.5 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
                disabled={!imageUrl.trim() && !imageFile}
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table insertion dialog */}
      {showTableDialog && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className={clsx(
            'w-full max-w-sm rounded-xl shadow-xl border p-6 space-y-4',
            wordTheme === 'dark'
              ? 'bg-slate-900 border-slate-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-900'
          )}>
            <h3 className="text-lg font-semibold">Insert Table</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="block text-sm font-medium">
                Rows
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={tableRows}
                  onChange={(e) => setTableRows(parseInt(e.target.value || '1', 10))}
                  className={clsx(
                    'mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    wordTheme === 'dark'
                      ? 'bg-slate-800 border-slate-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  )}
                />
              </label>
              <label className="block text-sm font-medium">
                Columns
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={tableCols}
                  onChange={(e) => setTableCols(parseInt(e.target.value || '1', 10))}
                  className={clsx(
                    'mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    wordTheme === 'dark'
                      ? 'bg-slate-800 border-slate-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  )}
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowTableDialog(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTableInsert}
                className="px-3 py-1.5 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WordEditor
